import React, { useEffect, useRef } from 'react'
import { tv } from 'tailwind-variants'
import { Minus, Tick } from 'components/icons'

// In the manner of shadcn/ui's checkbox: a 16px box with a light 1px border (faintly filled in the
// dark theme, like a text field) that fills blue, with a white tick, when checked. The real checkbox sits over the box, invisible, so
// it takes the clicks and the keyboard focus, and the box shows its state and a focus ring. A disabled
// box is faded rather than emptied, so a disabled, checked box still reads as checked. Partly checked
// (a box for a group, some of whose items are ticked) fills blue too, with a dash for the tick.
const checkbox = tv({
  slots: {
    container: 'items-center gap-2 my-2.5 select-none leading-[150%] cursor-pointer',
    control: 'relative inline-flex shrink-0 size-4',
    input: 'peer absolute inset-0 m-0 size-full opacity-0 cursor-pointer',
    box: [
      'flex size-4 items-center justify-center rounded-[4px] border border-divider bg-field',
      'text-white transition-colors duration-100',
      'peer-checked:border-blue peer-checked:bg-blue peer-indeterminate:border-blue peer-indeterminate:bg-blue',
      'peer-focus-visible:ring-3 peer-focus-visible:ring-blue/25',
      '[&>svg]:size-3 [&>svg]:stroke-[3] [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100',
      'peer-indeterminate:[&>svg]:opacity-100'
    ]
  },
  variants: {
    // With a label it takes a line of its own; on its own, it sits inline with whatever's beside it.
    labelled: {
      true: { container: 'flex' },
      false: { container: 'inline-flex' }
    },
    disabled: {
      true: {
        container: 'cursor-not-allowed',
        input: 'cursor-not-allowed',
        box: 'opacity-50'
      }
    }
  }
})

interface CheckboxProps {
  defaultChecked?: boolean
  onChange: (event: any) => void
  children?: any
  checked?: boolean
  dataCy?: string
  disabled?: boolean
  /** Partly checked: some, but not all, of what the box stands for is ticked. */
  indeterminate?: boolean
  /** The box's name, for a box with no label beside it. */
  label?: string
  className?: string
}

const Checkbox: React.FC<CheckboxProps> = ({
  defaultChecked,
  onChange,
  children,
  checked,
  dataCy,
  disabled,
  indeterminate = false,
  label,
  className
}) => {
  const hasChildren = React.Children.count(children) > 0
  const styles = checkbox({ labelled: hasChildren, disabled: !!disabled })
  const input = useRef<HTMLInputElement>(null)
  // Partly checked is a property of the element alone; there's no attribute for it.
  useEffect(() => {
    if (input.current) input.current.indeterminate = indeterminate
  }, [indeterminate])
  return (
    <div>
      <label className={styles.container({ class: className })}>
        <span className={styles.control()}>
          <input
            ref={input}
            type="checkbox"
            data-slot="checkbox"
            // Controlled when the caller passes checked; otherwise it keeps its own state.
            {...(checked === undefined ? { defaultChecked } : { checked })}
            onChange={onChange}
            disabled={!!disabled}
            aria-label={label}
            className={styles.input()}
          />
          <span className={styles.box()} data-cy={dataCy} aria-hidden>
            {indeterminate ? <Minus /> : <Tick />}
          </span>
        </span>
        {children}
      </label>
    </div>
  )
}

export default Checkbox
